// Where a sign-in may send the user back to (docs/Auth-Flows.md, "Where a
// sign-in lands"): a path on this site, never an absolute URL and never a
// sign-in page, which would only start the sign-in again.

const SIGN_IN_PATHS = ['/login', '/signin', '/signed-out', '/auth/callback'];
const BASE = 'https://mashlom.invalid';

type LocationLike = { pathname?: string; search?: string; hash?: string } | null | undefined;

export const safeReturnPath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  // The URL parser treats "\" as "/" and drops tabs and newlines, so "/\evil.com"
  // and "/\t/evil.com" both mean another host. Parse it and keep only this site.
  let url: URL;
  try {
    url = new URL(value, BASE);
  } catch {
    return null;
  }
  if (url.origin !== BASE) return null;
  // React Router matches paths regardless of case and a trailing slash.
  const pathname = url.pathname.toLowerCase().replace(/\/+$/, '') || '/';
  if (SIGN_IN_PATHS.includes(pathname)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
};

// The `from` location that ProtectedRoute and RootRedirect put in the state of
// their redirect to /login.
export const returnPathFromLocation = (from: LocationLike): string | null => (
  from ? safeReturnPath(`${from.pathname || ''}${from.search || ''}${from.hash || ''}`) : null
);

// Where a sign-in that still needs onboarding lands: the wizard, carrying the
// page to return to afterwards and a notice to show (e.g. an invitation that was
// signed up for with another email). Only the parameters that have a value.
export const onboardingPath = ({ returnTo = null, notice = null }: { returnTo?: unknown; notice?: string | null } = {}): string => {
  const params = new URLSearchParams();
  const safe = safeReturnPath(returnTo);
  if (safe) params.set('returnTo', safe);
  if (notice) params.set('notice', notice);
  const query = params.toString();
  return query ? `/onboarding/register?${query}` : '/onboarding/register';
};
